/**
 * StageStoryPage — V14.90 : vue client publique `/story/:token`.
 * Histoire déroulante : maquette blanche → matériaux → aménagement → lumière.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fullImg = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

const StorySection = ({ step, total }) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setSeen(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setSeen(true); }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <section ref={ref} className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_380px] gap-8 items-center px-6 lg:px-16 py-16" data-testid={`story-step-${step.stage}`}>
      <div className="relative rounded-2xl overflow-hidden" style={{ background: '#0F1115', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 30px 80px rgba(0,0,0,0.45)', opacity: seen ? 1 : 0, transform: seen ? 'translateY(0) scale(1)' : 'translateY(32px) scale(0.98)', transition: 'opacity 700ms ease, transform 700ms ease' }}>
        <img src={fullImg(step.image_url)} alt={step.title} className="w-full max-h-[82vh] object-contain block" loading="lazy" data-testid={`story-img-${step.stage}`} />
        <span className="absolute top-4 left-4 text-[11px] tracking-[0.25em] uppercase px-3 py-1 rounded-full" style={{ background: 'rgba(0,0,0,0.55)', color: '#F5C77E', backdropFilter: 'blur(8px)' }}>
          Étape {step.index} / {total}
        </span>
      </div>
      <div style={{ opacity: seen ? 1 : 0, transform: seen ? 'translateX(0)' : 'translateX(24px)', transition: 'opacity 700ms ease 150ms, transform 700ms ease 150ms' }}>
        <div className="text-[64px] leading-none font-light tabular-nums" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(245,199,126,0.35)' }}>0{step.index}</div>
        <h2 className="mt-2 text-2xl lg:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#F4F1EA' }}>{step.title}</h2>
        <p className="mt-3 text-[15px] leading-relaxed" style={{ color: 'rgba(244,241,234,0.72)' }}>{step.caption}</p>
        <dl className="mt-6 space-y-1.5 text-[12px]" style={{ color: 'rgba(244,241,234,0.55)' }}>
          {step.vue_angle && <div className="flex gap-2"><dt className="uppercase tracking-wider w-24 shrink-0">Vue</dt><dd>{step.vue_angle}</dd></div>}
          {step.moment_journee && <div className="flex gap-2"><dt className="uppercase tracking-wider w-24 shrink-0">Lumière</dt><dd>{step.moment_journee}</dd></div>}
          {typeof step.fidelity_score === 'number' && <div className="flex gap-2"><dt className="uppercase tracking-wider w-24 shrink-0">Fidélité</dt><dd>{step.fidelity_score} % au plan</dd></div>}
        </dl>
      </div>
    </section>
  );
};

export default function StageStoryPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/story/${token}`).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || 'Lien invalide'));
  }, [token]);

  if (error) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0D11', color: '#F4F1EA' }} data-testid="story-error">{error}</div>;
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-sm" style={{ background: '#0B0D11', color: 'rgba(244,241,234,0.6)' }}>Chargement de l'histoire…</div>;
  }
  const total = data.steps.length;
  return (
    <main style={{ background: 'radial-gradient(1200px 600px at 20% -10%, rgba(217,160,91,0.10), transparent 60%), #0B0D11', color: '#F4F1EA' }} data-testid="story-page">
      <header className="min-h-[70vh] flex flex-col justify-end px-6 lg:px-16 pb-16 pt-24">
        <span className="text-[11px] tracking-[0.3em] uppercase" style={{ color: '#F5C77E' }}>Peter's Universe · Histoire d'un plan</span>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-4xl" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="story-title">
          {data.project?.name || 'Votre projet'}{data.lot?.lot_number ? ` — Lot ${data.lot.lot_number}` : ''}
        </h1>
        <p className="mt-4 text-base md:text-lg max-w-2xl" style={{ color: 'rgba(244,241,234,0.7)' }}>
          {data.project?.address ? `${data.project.address} · ` : ''}Du trait d'architecte à l'image finale, en {total} étape{total > 1 ? 's' : ''}. Faites défiler.
        </p>
        <div className="mt-10 flex gap-2">
          {data.steps.map((s) => <span key={s.stage} className="h-1 rounded-full" style={{ width: 48, background: 'rgba(245,199,126,0.5)' }} />)}
        </div>
      </header>
      {data.steps.map((s) => <StorySection key={s.stage} step={s} total={total} />)}
      <footer className="px-6 lg:px-16 py-16 text-[12px]" style={{ color: 'rgba(244,241,234,0.45)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        Images de synthèse — simulation 3D non contractuelle. Générées à partir du plan vectorisé du lot.
      </footer>
    </main>
  );
}
